/**
 * Phone number utility functions for consistent formatting across the application
 */

/**
 * Format a phone number for display as (000) 000-0000
 * @param value The raw phone number input
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format as (000) 000-0000
  if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

/**
 * Convert formatted phone number to backend format (e.g., +13175084217)
 * @param formattedPhone The formatted phone number string
 * @returns Phone number in backend format or undefined if empty
 */
export const convertPhoneToBackendFormat = (formattedPhone: string): string | undefined => {
  if (!formattedPhone || !formattedPhone.trim()) {
    return undefined;
  }
  
  // Remove all non-digits
  const digits = formattedPhone.replace(/\D/g, '');
  
  // If it's a 10-digit US number, add country code 1 and + prefix
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  
  // If it already has country code (11 digits starting with 1), add + prefix
  // Fixed: Previously missing + prefix which caused "Invalid phone number format" API errors
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  
  // If it already has the + prefix, return as is
  if (formattedPhone.startsWith('+')) {
    return formattedPhone;
  }
  
  // For any other format, add + prefix to digits (backend will handle validation)
  return `+${digits}`;
}; 